import * as types from './actionTypes';
import * as authTypes from '../user/actionTypes';
// import GeoFeatureActions from '../geoFeature/actions'
import UserActions from '../user/actions';
import AgentRegistryActions from '../agentRegistry/actions';

import SearchClient from '../../focus-client/search-client';
import ObservationClient from '../../focus-client/crud-client/observation-client';
import GeoFeatureClient from '../../focus-client/crud-client/geofeature-client';
import moment from 'moment';

import { createQuery } from '../../utils/queryUtil';

const setAggregatesFetching = value => ({
  type: types.SET_AGGREGATES_FETCHING,
  payload: value
});

const getAggregates = () => {
  // Reads the dateTime dynamic facet from the standard facets endpoint.
  // Geert's server-side implementation returns year/month/day buckets
  // automatically based on the active date range — we just need to
  // pluck response.facets.dateTime and store it as aggregates.dateHistogram
  // so HistogramComponent can render it without any client-side bucketing.
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    dispatch(setAggregatesFetching(true));
    let search = SearchClient(config);

    // Reuse the same query shape as getDataSources / getAgents
    const query = buildFacetQuery(getState, {
      withDataSources: true,
      withAgents: true
    });

    const state = getState();
    return search
      .facetsDateTime(
        query,
        'get-histogram',
        state.query.start,
        state.query.end
      )
      .then(response => {
        if (!response.isError) {
          dispatch(
            setAggregates(response ? { dateHistogram: response.facet } : {})
          );
        } else {
          dispatch(setAggregatesFetching(false));
          if (response.error && response.error.status === 401) {
            dispatch(localLogout());
          }
        }
      })
      .catch(() => {
        dispatch(setAggregatesFetching(false));
      });
  };
};

const localLogout = () => ({
  type: authTypes.LOCAL_LOGOUT
});

const setAggregates = values => ({
  type: types.SET_AGGREGATES,
  payload: values
});

const startTimelineFetch = () => ({
  type: types.START_TIMELINE_FETCH,
  payload: null
});

const stopTimelineFetch = () => ({
  type: types.STOP_TIMELINE_FETCH,
  payload: null
});

const setScrollToTop = value => ({
  type: types.SET_SCROLL_TO_TOP,
  payload: value
});

const setResultTypeCounts = value => ({
  type: types.SET_RESULT_TYPE_COUNTS,
  payload: value
});

const buildResultTypeCountSignature = queryState => {
  const state = queryState || {};
  return JSON.stringify({
    north: state.north,
    east: state.east,
    south: state.south,
    west: state.west,
    start: state.start,
    end: state.end,
    searchQuery: state.searchQuery || '',
    concepts: state.concepts || [],
    dataSources: state.dataSources || [],
    agents: state.agents || [],
    mode: state.mode
  });
};

const buildTimelineQueryBase = getState => {
  const state = getState();
  let coordinates = {
    north: state.query.north,
    east: state.query.east,
    south: state.query.south,
    west: state.query.west
  };

  const query = createQuery(
    false,
    true,
    true,
    false,
    true,
    0,
    coordinates,
    state.query.areas,
    state.query.concepts,
    state.query.mode,
    state.query.dataSources,
    state.query.agents
  );

  if (state.query.start !== null && state.query.end !== null) {
    query.withDateTimeRange(state.query.end, state.query.start);
  }
  return query;
};

const buildFacetQuery = (getState, options = {}) => {
  const { withDataSources = false, withAgents = false } = options;
  const state = getState();
  const coordinates = {
    north: state.query.north,
    east: state.query.east,
    south: state.query.south,
    west: state.query.west
  };

  // Keep facet lists aligned with active filters while excluding the facet's
  // own dimension so users can still discover available values.
  const query = createQuery(
    false,
    true,
    withDataSources,
    true,
    withAgents,
    null,
    coordinates,
    state.query.areas,
    state.query.concepts,
    state.query.mode,
    state.query.dataSources,
    state.query.agents
  );

  if (state.query.searchQuery !== '') {
    query.withQueryText(state.query.searchQuery);
  }
  if (state.query.start !== null && state.query.end !== null) {
    query.withDateTimeRange(state.query.end, state.query.start);
  }
  return query;
};

const combineQueryText = (searchQuery, extraFilter) => {
  const base = String(searchQuery || '').trim();
  const extra = String(extraFilter || '').trim();
  if (base && extra) return `(${base}) AND (${extra})`;
  return extra || base || '';
};

const getResultTypeCounts = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return async (dispatch, getState) => {
    const search = SearchClient(config);
    const selectedLanguage = getState().ontology.selectedLanguage;
    const searchQuery = getState().query.searchQuery;
    const requestSignature = buildResultTypeCountSignature(getState().query);
    const fetchTotal = async (entityTypes, extraFilter) => {
      const query = buildTimelineQueryBase(getState);
      query.withEntities(entityTypes, 'or');
      query.options.withPageLength(1);
      const queryText = combineQueryText(searchQuery, extraFilter);
      if (queryText) {
        query.withQueryText(queryText);
      }
      const response = await search.results(
        query,
        selectedLanguage,
        'get-result-type-counts'
      );
      if (response && !response.isError) {
        return Math.max(0, Number(response.total) || 0);
      }
      if (response && response.error && response.error.status === 401) {
        dispatch(localLogout());
      }
      return 0;
    };

    const fetchAlertTotal = async () => {
      const entityClassAlert = await fetchTotal(
        ['Observation'],
        'entityClass:alert'
      );
      if (entityClassAlert > 0) return entityClassAlert;
      // Keep compatibility with backends still indexing alert in observationClass.
      const observationClassAlert = await fetchTotal(
        ['Observation'],
        'observationClass:"alert"'
      );
      return Math.max(entityClassAlert, observationClassAlert);
    };

    const [tracks, alerts, notes] = await Promise.all([
      fetchTotal(['track'], ''),
      fetchAlertTotal(),
      fetchTotal(['Observation'], 'entityClass:note')
    ]);

    const currentSignature = buildResultTypeCountSignature(getState().query);
    if (currentSignature !== requestSignature) {
      return;
    }

    const observationsCombined = alerts + notes;
    dispatch(
      setResultTypeCounts({
        tracks,
        observationsCombined,
        alerts,
        notes,
        total: tracks + observationsCombined,
        querySignature: requestSignature
      })
    );
  };
};

const getAlertTimeline = scroll => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let search = SearchClient(config);
    let observation = ObservationClient(config);

    let alertQuery = createQuery(
      scroll,
      true,
      true,
      true,
      true,
      getState().timeline.results.length,
      null,
      getState().query.areas,
      [],
      getState().query.mode,
      [],
      []
    );

    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    alertQuery.withDateTimeRange(now, oneHourAgo);
    // get alerts

    return search
      .results(alertQuery, getState().ontology.selectedLanguage, 'get-alerts')
      .then(alertResponse => {
        if (!alertResponse.isError) {
          alertResponse.results = alertResponse.results.filter(item => {
            let foundObservation = item.extracted.content.find(val => {
              return val.Observation?.observationClass === 'alert';
            });
            return !!foundObservation;
          });
          alertResponse.total = alertResponse.results.length;

          dispatch(setAlerts(alertResponse));

          alertResponse.results.forEach(item => {
            observation
              .media(item.id, getState().ontology.selectedLanguage)
              .then(mediaResponse => {
                if (mediaResponse.length > 0) {
                  dispatch(setAlertObservationMedia(item.id, mediaResponse));
                }
              });
          });
        } else if (alertResponse.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const getTimeline = scroll => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    const formatDuration = durationMs => `${(durationMs / 1000).toFixed(2)}s`;
    dispatch(startTimelineFetch());
    let search = SearchClient(config);
    let observation = ObservationClient(config);
    let coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };

    const requestSnapshot = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west,
      start: getState().query.start,
      end: getState().query.end,
      searchQuery: getState().query.searchQuery,
      concepts: JSON.stringify(getState().query.concepts || []),
      dataSources: JSON.stringify(getState().query.dataSources || []),
      agents: JSON.stringify(getState().query.agents || []),
      mode: getState().query.mode
    };

    const query = createQuery(
      scroll,
      true,
      true,
      true,
      true,
      getState().timeline.results.length,
      coordinates,
      getState().query.areas,
      getState().query.concepts,
      getState().query.mode,
      getState().query.dataSources,
      getState().query.agents
    );
    query.options.withPageLength(25);
    if (getState().query.searchQuery !== '')
      query.withQueryText(getState().query.searchQuery);
    if (getState().query.start !== null && getState().query.end !== null)
      query.withDateTimeRange(getState().query.end, getState().query.start);

    const resultsNetworkStartedAt = performance.now();
    return search
      .results(query, getState().ontology.selectedLanguage, 'get-timeline')
      .then(response => {
        const networkDurationMs = Math.max(
          0,
          performance.now() - resultsNetworkStartedAt
        );
        // eslint-disable-next-line no-console
        console.info(
          `[getTimeline] results network: ${formatDuration(networkDurationMs)}`
        );

        const normalizeStoreStartedAt = performance.now();
        try {
          const stateNow = getState().query;
          const isStaleResponse =
            stateNow.north !== requestSnapshot.north ||
            stateNow.east !== requestSnapshot.east ||
            stateNow.south !== requestSnapshot.south ||
            stateNow.west !== requestSnapshot.west ||
            stateNow.start !== requestSnapshot.start ||
            stateNow.end !== requestSnapshot.end ||
            stateNow.searchQuery !== requestSnapshot.searchQuery ||
            JSON.stringify(stateNow.concepts || []) !==
              requestSnapshot.concepts ||
            JSON.stringify(stateNow.dataSources || []) !==
              requestSnapshot.dataSources ||
            JSON.stringify(stateNow.agents || []) !== requestSnapshot.agents ||
            stateNow.mode !== requestSnapshot.mode;
          if (isStaleResponse) {
            dispatch(stopTimelineFetch());
            return;
          }

          if (!response.isError) {
            dispatch(setTimeline(response, !scroll));
            if (!scroll) {
              dispatch(getResultTypeCounts());
            }
            response.results.forEach(item => {
              const foundObservation = item.extracted.content.find(val => {
                return !!val.Observation;
              });
              if (!foundObservation) return;
              observation
                .media(item.id, getState().ontology.selectedLanguage)
                .then(mediaResponse => {
                  if (Array.isArray(mediaResponse)) {
                    dispatch(setObservationMedia(item.id, mediaResponse));
                  }
                });
            });
            dispatch(UserActions.setLastKnownUpdate());
          } else if (response.error.status === 401) {
            dispatch(localLogout());
            dispatch(stopTimelineFetch());
          } else {
            dispatch(stopTimelineFetch());
          }
        } finally {
          const normalizeStoreDurationMs = Math.max(
            0,
            performance.now() - normalizeStoreStartedAt
          );
          // eslint-disable-next-line no-console
          console.info(
            `[getTimeline] results normalize/store: ${formatDuration(
              normalizeStoreDurationMs
            )}`
          );
        }
      })
      .catch(() => {
        dispatch(stopTimelineFetch());
      });
  };
};

const setTimeline = (values, replace = false) => ({
  type: types.SET_TIMELINE_SUCCESS,
  payload: {
    ...values,
    _replace: Boolean(replace)
  }
});

const setObservationMedia = (observationId, values) => ({
  type: types.SET_OBSERVATION_MEDIA,
  payload: { observationId, values }
});

const setAlerts = values => ({
  type: types.SET_ALERTS,
  payload: values
});

const setAlertObservationMedia = (observationId, values) => ({
  type: types.SET_ALERT_OBSERVATION_MEDIA,
  payload: { observationId, values }
});

const parseObservationTimestamp = (observation, result) => {
  const candidates = [
    observation && observation.when,
    observation && observation.observationDateTime,
    observation && observation.dateTime,
    result && result.when,
    result && result.datetime
  ];
  const found = candidates.find(value => value !== null && value !== undefined);
  if (!found) return null;
  const parsed = moment(found);
  return parsed.isValid() ? parsed : null;
};

const syncAlertsFromTimelineResults = () => (dispatch, getState) => {
  const now = moment();
  const oneHourAgo = moment(now).subtract(1, 'hour');
  const results = getState().timeline.results || [];

  const recentAlerts = results.filter(result => {
    const foundObservation = result?.extracted?.content?.find(
      value => value && value.Observation
    );
    const observation = foundObservation && foundObservation.Observation;
    if (!observation) return false;
    if (String(observation.observationClass || '').toLowerCase() !== 'alert') {
      return false;
    }
    const timestamp = parseObservationTimestamp(observation, result);
    if (!timestamp) return false;
    return timestamp.isSameOrAfter(oneHourAgo) && timestamp.isSameOrBefore(now);
  });

  dispatch(
    setAlerts({
      results: recentAlerts,
      total: recentAlerts.length,
      start: 1
    })
  );
};

const getSingleItem = value => {
  var config = {
    baseURI: document.baseURI
  };

  return (dispatch, getState) => {
    let observation = ObservationClient(config);

    return observation
      .read(value, getState().ontology.selectedLanguage)
      .then(async response => {
        if (!response.isError) {
          const rawObservation = response.envelope.instance.Observation;
          const selectedObservation = rawObservation
            ? {
                ...rawObservation,
                observationId:
                  rawObservation.observationId ||
                  rawObservation.ObservationId ||
                  rawObservation.EntityId
              }
            : rawObservation;
          dispatch(setSelectedItem(selectedObservation));

          let mediaResponse = [];
          try {
            const media = await observation.media(
              value,
              getState().ontology.selectedLanguage
            );
            mediaResponse = Array.isArray(media) ? media : [];
          } catch (error) {
            mediaResponse = [];
          }
          dispatch(setSelectedObservationMedia(mediaResponse));
          return {
            ...selectedObservation,
            media: mediaResponse
          };
        } else if (response.error.status === 401) {
          dispatch(localLogout());
        }
        return null;
      });
  };
};

const setSelectedObservationMedia = values => ({
  type: types.SET_SELECTED_OBSERVATION_MEDIA,
  payload: values
});

const getSingleTrackItem = value => {
  var config = {
    baseURI: document.baseURI
  };

  return (dispatch, getState) => {
    let geoFeature = GeoFeatureClient(config);

    return geoFeature
      .read(value, getState().ontology.selectedLanguage)
      .then(response => {
        if (!response.isError) {
          dispatch(setSelectedItem(response.envelope.instance.GeoFeature));
        } else if (response.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const getDataSources = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let search = SearchClient(config);
    const query = buildFacetQuery(getState, {
      withDataSources: false,
      withAgents: true
    });

    return search.facets(query, 'get-data-sources').then(response => {
      if (!response.isError) {
        dispatch(setDataSources(response));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
      return response;
    });
  };
};

const setDataSources = value => ({
  type: types.SET_DATA_SOURCES,
  payload: value
});

const getAgents = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let search = SearchClient(config);
    const query = buildFacetQuery(getState, {
      withDataSources: true,
      withAgents: false
    });

    return search.facets(query, 'get-agents').then(response => {
      if (!response.isError) {
        dispatch(setAgents(response));
        // Resolve sensor registration names for any agent id we have not seen
        // before. No-op once the cache is warm.
        const facetValues =
          response.facets &&
          response.facets.agents &&
          response.facets.agents.facetValues
            ? response.facets.agents.facetValues
            : [];
        if (facetValues.length) {
          dispatch(
            AgentRegistryActions.fetchMissingAgentRegistrations(facetValues)
          );
        }
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
      return response;
    });
  };
};

const setAgents = value => ({
  type: types.SET_AGENTS,
  payload: value
});

const clearTimelineResults = () => ({
  type: types.CLEAR_TIMELINE_RESULTS,
  payload: null
});

const clearTimelineFacets = () => ({
  type: types.CLEAR_TIMELINE_FACETS,
  payload: null
});

const setSelectedItem = value => (dispatch, getState) => {
  const alertTimelineOpen = getState().ontology.alertTimelineOpen;
  if (value && value.observationId) {
    dispatch(markObservationViewed(value.observationId));
  }
  if (value && value.featureId) {
    dispatch(markTrackViewed(value.featureId));
  }
  dispatch({
    type: types.SET_SELECTED_ITEM,
    payload: value,
    alertTimelineOpen: alertTimelineOpen
  });
};

const clearSelectedItem = () => ({
  type: types.CLEAR_SELECTED_ITEM,
  payload: null
});

const allowAlertSounds = () => ({
  type: types.ALLOW_ALERT_SOUNDS,
  payload: null
});

const disableAlertSounds = () => ({
  type: types.DISABLE_ALERT_SOUNDS,
  payload: null
});

const markObservationViewed = observationId => ({
  type: types.MARK_OBSERVATION_VIEWED,
  payload: observationId
});

const markTrackViewed = trackId => ({
  type: types.MARK_TRACK_VIEWED,
  payload: trackId
});

export default {
  getAggregates,
  getTimeline,
  getAlertTimeline,
  startTimelineFetch,
  setScrollToTop,
  getSingleItem,
  getSingleTrackItem,
  // getFacets,
  getDataSources,
  getAgents,
  syncAlertsFromTimelineResults,
  getResultTypeCounts,
  clearTimelineResults,
  clearTimelineFacets,
  setSelectedItem,
  clearSelectedItem,
  allowAlertSounds,
  disableAlertSounds,
  markObservationViewed,
  markTrackViewed,
  setAlertObservationMedia
};
